import type { HTMLAttributes, Ref } from "react";
import {
  ChevronDown,
  ChevronRight,
  ChevronUp,
  GripVertical,
  Power,
  Trash2,
} from "lucide-react";
import type { SortIndicatorColors } from "../SortableListsManager/sortableLists";

export type CustomListSortItemVariant = "list" | "category" | "child";

export type CustomListSortItemProps = {
  className?: string;
  label: string;
  info?: string;
  position?: number;
  variant?: CustomListSortItemVariant;
  active?: boolean;
  protectedItem?: boolean;
  isDragging?: boolean;
  isOverlay?: boolean;
  expanded?: boolean;
  sortIndicatorColors?: SortIndicatorColors;
  dragHandleRef?: Ref<HTMLButtonElement>;
  dragHandleProps?: HTMLAttributes<HTMLButtonElement>;
  onToggleActive?: () => void;
  onDelete?: () => void;
  onOpen?: () => void;
};

export default function CustomListSortItem({
  className = "",
  label,
  info,
  position,
  variant = "list",
  active = true,
  protectedItem = false,
  isDragging = false,
  isOverlay = false,
  expanded,
  sortIndicatorColors,
  dragHandleRef,
  dragHandleProps,
  onToggleActive,
  onDelete,
  onOpen,
}: CustomListSortItemProps) {
  const isCategory = variant === "category";
  const isChild = variant === "child";
  const foreground = isCategory ? "text-white" : "text-[#273552]";
  const iconButton = `inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md transition-colors ${
    isCategory
      ? "text-white/90 hover:bg-white/15 hover:text-white disabled:text-white/35"
      : "text-[#273552]/70 hover:bg-[#273552]/10 hover:text-[#273552] disabled:text-[#273552]/25"
  }`;

  return (
    <div
      className={`w-full ${isChild ? "pl-3" : ""} ${isDragging ? "opacity-30" : ""}`}
      data-variant={variant}
    >
      <div
        className={`flex min-h-[67px] w-full items-center justify-between gap-3 overflow-hidden border px-[11px] py-[11px] shadow-sm transition-[color,background-color,border-color,box-shadow] ${
          isCategory
            ? "rounded-[17px] border-[#fafbfc] bg-[#a0a0a0]"
            : "rounded-[17px] border-[#273552] bg-white"
        } ${active ? "" : "opacity-55"} ${isOverlay ? "shadow-xl" : ""} ${className}`}
      >
        <div className="flex min-w-0 flex-1 items-center gap-[11px]">
          {protectedItem ? (
            <span className="h-8 w-5 shrink-0" aria-hidden="true" />
          ) : (
            <button
              ref={dragHandleRef}
              type="button"
              aria-label={`Reorder ${label}`}
              title={`Reorder ${label}`}
              className={`inline-flex h-8 w-5 shrink-0 touch-none items-center justify-center cursor-grab active:cursor-grabbing ${foreground}`}
              {...dragHandleProps}
            >
              <GripVertical className="h-5 w-5" aria-hidden="true" />
            </button>
          )}

          {!isCategory && position !== undefined && (
            <span
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#c5d9e8] text-base font-medium text-[#273552]"
              style={
                sortIndicatorColors
                  ? {
                      background: sortIndicatorColors.background,
                      color: sortIndicatorColors.foreground,
                    }
                  : undefined
              }
            >
              {position}
            </span>
          )}

          <div className={`min-w-0 ${foreground}`}>
            <div className="truncate text-base leading-6">{label}</div>
            {info && (
              <div className="truncate text-xs leading-[18px] opacity-90">
                {info}
              </div>
            )}
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-1 sm:gap-4">
          <button
            type="button"
            className={iconButton}
            onClick={onToggleActive}
            disabled={!onToggleActive}
            aria-label={`${active ? "Disable" : "Enable"} ${label}`}
            title={`${active ? "Disable" : "Enable"} ${label}`}
          >
            <Power className="h-5 w-5" aria-hidden="true" />
          </button>
          <button
            type="button"
            className={iconButton}
            onClick={onDelete}
            disabled={!onDelete}
            aria-label={`Delete ${label}`}
            title={`Delete ${label}`}
          >
            <Trash2 className="h-5 w-5" aria-hidden="true" />
          </button>
          <button
            type="button"
            className={iconButton}
            onClick={onOpen}
            disabled={!onOpen}
            aria-label={
              expanded === undefined
                ? `Open ${label}`
                : `${expanded ? "Collapse" : "Expand"} ${label}`
            }
            title={
              expanded === undefined
                ? `Open ${label}`
                : `${expanded ? "Collapse" : "Expand"} ${label}`
            }
          >
            {expanded === undefined ? (
              <ChevronRight className="h-5 w-5" aria-hidden="true" />
            ) : expanded ? (
              <ChevronUp className="h-5 w-5" aria-hidden="true" />
            ) : (
              <ChevronDown className="h-5 w-5" aria-hidden="true" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
